// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig, loginRequest, tokenRequest } from "../authConfig";
import {
  registerAuthTokenFunction,
  registerTokenRefreshFunction,
  registerLogoutFunction,
  setTokenExpirationFromClaims
} from "../submodule/src/utils/api/auth-bridge";
import {
  initEmbedAuthListener,
  sendReady,
  getEmbedToken,
  requestTokenRefresh,
  destroyEmbedAuth
} from "../submodule/src/utils/embed-auth-bridge";
import { getClientCredentialsToken } from "../submodule/src/utils/api/client-credentials-token";
import { logger } from "../submodule/src/utils/logger";

const AUTH_MODE = (process.env.REACT_APP_AUTH_MODE || "").toLowerCase();
const isClientCredentialsMode = AUTH_MODE === "client_credentials";

// True if production environment (run with npm run build)(dev, test, imp, prod)
// or if REACT_APP_FORCE_OAUTH=true
const shouldUseOAuth =
  !isClientCredentialsMode &&
  (process.env.NODE_ENV === 'production' || process.env.REACT_APP_FORCE_OAUTH === 'true');

// Embed mode: front-chat runs inside an iframe, auth comes from parent via postMessage
const isEmbedMode = new URLSearchParams(window.location.search).get("embed") === "true";

// Lazy MSAL init: skip instantiation entirely in embed mode (not needed, avoids overhead)
let msalInstance = null;
function getMsalInstance() {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

// Session key to track if we've already attempted a redirect this session
const REDIRECT_ATTEMPTED_KEY = "auth_redirect_attempted";
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [embedAuthError, setEmbedAuthError] = useState(null);

  /** GET VALID ACCESS TOKEN */
  const getValidAccessToken = useCallback(async (options = {}) => {
    logger.debug("[Auth] getValidAccessToken called");

    if (isClientCredentialsMode) {
      const tokenResponse = await getClientCredentialsToken(options);
      setToken(tokenResponse.accessToken);
      setTokenExpirationFromClaims(tokenResponse.idTokenClaims);
      return tokenResponse;
    }

    if (!shouldUseOAuth) {
      logger.debug("[Auth] Local environment - returning null (no OAuth)");
      return null;
    }

    const { forceRefresh = false, silentOnly = false } = options;
    const account = getMsalInstance().getActiveAccount() || getMsalInstance().getAllAccounts()[0];

    if (!account) {
      logger.debug("[Auth] No account found, redirecting to login");
      await getMsalInstance().loginRedirect(loginRequest);
      return null;
    }

    logger.debug("[Auth] Account found:", account);

    /**
     * NOTE: We use ID token. DO NOT change this behavior without coordination with the backend.
     */
    if (!forceRefresh && account.idToken && account.idTokenClaims) {
      logger.debug("[Auth] ✔ Using ID token from cached account");
      logger.debug("[Auth]   - ID Token available:", !!account.idToken);
      logger.debug("[Auth]   - ID Token Claims available:", !!account.idTokenClaims);

      // Clear redirect flag on successful token retrieval (allows future redirects if token expires)
      sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);

      setTokenExpirationFromClaims(account.idTokenClaims);

      // Return ID token and claims
      return {
        accessToken: account.idToken,
        idToken: account.idToken,
        idTokenClaims: account.idTokenClaims
      };
    }

    try {
      logger.debug("[Auth] Attempting silent token acquisition for scopes:", tokenRequest.scopes);
      const resp = await getMsalInstance().acquireTokenSilent({
        ...tokenRequest,
        account,
        forceRefresh
      });

      logger.debug("[Auth] Silent token response:", resp);

      if (!resp?.accessToken && !resp?.idToken) {
        // Prevent infinite redirect loop: only redirect once per session
        if (silentOnly) {
          logger.warn("[Auth] Silent token refresh failed without tokens. Returning cached token.");
          if (account.idToken && account.idTokenClaims) {
            setTokenExpirationFromClaims(account.idTokenClaims);
            return {
              accessToken: account.idToken,
              idToken: account.idToken,
              idTokenClaims: account.idTokenClaims
            };
          }
          return null;
        }
        if (sessionStorage.getItem(REDIRECT_ATTEMPTED_KEY)) {
          logger.warn("[Auth] No tokens in response, but redirect already attempted this session. Skipping redirect.");
          return null;
        }
        logger.warn("[Auth] No tokens in response, triggering interactive consent");
        sessionStorage.setItem(REDIRECT_ATTEMPTED_KEY, "true");
        await getMsalInstance().acquireTokenRedirect({ ...tokenRequest, account });
        return null;
      }

      logger.debug("[Auth] ✔ Token acquired successfully");
      logger.debug("[Auth]   - Scopes:", resp.scopes);
      logger.debug("[Auth]   - ID Token available:", !!resp.idToken);

      // Clear redirect flag on successful token acquisition
      // (allows future redirects if token expires)
      sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);

      // Return ID token
      const tokenToUse = resp.idToken;
      setToken(tokenToUse);
      setTokenExpirationFromClaims(resp.idTokenClaims);
      return {
        accessToken: tokenToUse,
        idToken: resp.idToken,
        idTokenClaims: resp.idTokenClaims
      };
    } catch (e) {
      logger.error(`[Auth] Token acquisition (silent=${silentOnly}) failed:`, e.errorCode || e.message);
      logger.error("[Auth] Full error:", e);
      if (silentOnly) {
        if (account.idToken && account.idTokenClaims) {
          setTokenExpirationFromClaims(account.idTokenClaims);
          return {
            accessToken: account.idToken,
            idToken: account.idToken,
            idTokenClaims: account.idTokenClaims
          };
        }
        return null;
      }
      // Any silent failure (interaction_required, consent_required, login_required,
      // timed_out from a blocked/unresponsive silent SSO iframe, or anything else MSAL
      // throws here) means we couldn't get a valid token in the background — fall back
      // to an interactive redirect rather than returning null with no recovery path.
      // Prevent infinite redirect loop: only redirect once per session
      if (sessionStorage.getItem(REDIRECT_ATTEMPTED_KEY)) {
        logger.warn("[Auth] Silent token acquisition failed, but redirect already attempted this session. Skipping redirect.");
        return null;
      }
      logger.debug("[Auth] Silent token acquisition failed, redirecting for re-authentication...");
      sessionStorage.setItem(REDIRECT_ATTEMPTED_KEY, "true");
      await getMsalInstance().acquireTokenRedirect({ ...tokenRequest, account });
      return null;
    }
  }, []);

  /** REFRESH TOKEN */
  const refreshToken = useCallback(async () => {
    if (isClientCredentialsMode) {
      await getValidAccessToken({ forceRefresh: true, silentOnly: true });
      return;
    }

    if (!shouldUseOAuth) {
      logger.debug("[Auth] Local environment - skipping token refresh");
      return;
    }

    // Prevent infinite redirect loop
    if (sessionStorage.getItem(REDIRECT_ATTEMPTED_KEY)) {
      logger.warn("[Auth] refreshToken called, but redirect already attempted this session. Skipping redirect.");
      return;
    }

    const account = getMsalInstance().getActiveAccount() || getMsalInstance().getAllAccounts()[0];
    if (account) {
      logger.debug("[Auth] refreshToken Redirect");
      sessionStorage.setItem(REDIRECT_ATTEMPTED_KEY, "true");
      await getMsalInstance().acquireTokenRedirect({ ...tokenRequest, account });
    } else {
      logger.debug("[Auth] refreshToken No Account, loginRedirect triggered");
      sessionStorage.setItem(REDIRECT_ATTEMPTED_KEY, "true");
      await getMsalInstance().loginRedirect(loginRequest);
    }
  }, [getValidAccessToken]);

  /** LOGIN */
  const login = useCallback(async () => {
    if (isClientCredentialsMode) {
      logger.debug("[Auth] Client credentials mode - skipping login");
      return;
    }

    if (!shouldUseOAuth) {
      logger.debug("[Auth] Local environment - skipping login");
      return;
    }

    logger.info("[Auth] loginRedirect triggered");
    await getMsalInstance().initialize();
    await getMsalInstance().loginRedirect(loginRequest);
  }, []);

  /** LOGOUT */
  const logout = useCallback(() => {
    if (isClientCredentialsMode) {
      logger.debug("[Auth] Client credentials mode - skipping logout");
      return;
    }

    if (!shouldUseOAuth) {
      logger.debug("[Auth] Local environment - skipping logout");
      return;
    }

    // Clear redirect flag so next login can redirect if needed
    sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);

    logger.info("[Auth] logoutRedirect triggered");
    getMsalInstance().logoutRedirect();
  }, []);

  // CHECK IF USER IS AUTHENTICATED
  const isAuthenticated = useCallback(() => {
    if (isClientCredentialsMode) {
      return true;
    }

    if (!shouldUseOAuth) {
      return true;
    }
    if (isEmbedMode) {
      return initialized;
    }
    return !!getMsalInstance().getActiveAccount();
  }, [initialized]);

  /** INITIALIZE MSAL INSTANCE */
  useEffect(() => {
    let cbId;
    (async () => {
      if (isClientCredentialsMode) {
        logger.debug("[Auth] Client credentials mode - skipping MSAL initialization");
        registerAuthTokenFunction(getValidAccessToken);
        registerTokenRefreshFunction(refreshToken);
        registerLogoutFunction(logout);

        try {
          const tokenResponse = await getValidAccessToken({ forceRefresh: true, silentOnly: true });
          setTokenExpirationFromClaims(tokenResponse.idTokenClaims);
          setToken(tokenResponse.accessToken);
        } catch (e) {
          logger.error("[Auth] Client credentials mode - failed to get initial token:", e.message);
          setEmbedAuthError(e.message);
        }

        setInitialized(true);
        setLoading(false);
        return;
      }

      if (!shouldUseOAuth) {
        logger.debug("[Auth] Local environment - skipping MSAL initialization");

        if (isEmbedMode) {
          logger.debug("[Auth] Local embed mode - initializing parent auth bridge");
          initEmbedAuthListener();
          sendReady();
        }

        // Register dummy auth functions for local environment
        registerAuthTokenFunction(async () => null);
        registerTokenRefreshFunction(async () => {});
        registerLogoutFunction(() => {});
        setInitialized(true);
        setLoading(false);
        return;
      }

      // Embed mode: receive auth from parent via postMessage, skip MSAL
      if (isEmbedMode) {
        logger.debug("[Auth] Embed mode - using parent auth via postMessage");
        initEmbedAuthListener();
        registerAuthTokenFunction(getEmbedToken);
        registerTokenRefreshFunction(requestTokenRefresh);
        registerLogoutFunction(() => {});
        sendReady();

        try {
          const tokenResponse = await getEmbedToken();
          logger.debug("[Auth] Embed mode - received initial token from parent");
          setTokenExpirationFromClaims(tokenResponse.idTokenClaims);
          setToken(tokenResponse.accessToken);
          setInitialized(true);
        } catch (e) {
          logger.error("[Auth] Embed mode - failed to get initial token:", e.message);
          setEmbedAuthError(e.message);
        }

        setLoading(false);
        return;
      }

      await getMsalInstance().initialize();

      // Process redirect response and set account if present
      const response = await getMsalInstance().handleRedirectPromise();
      logger.debug("[Auth] handleRedirectPromise response:", response);

      if (response?.account) {
        logger.debug("[Auth] Login successful - Account:", response.account.username);
        logger.debug("[Auth] ID Token Claims:", response.idTokenClaims);
        logger.debug("[Auth] Checking for custom claims:");
        logger.debug("[Auth]   - api_keys:", response.idTokenClaims?.api_keys);
        logger.debug("[Auth]   - groups:", response.idTokenClaims?.groups);
        logger.debugToken("[Auth] ID Token", response.idToken);
        getMsalInstance().setActiveAccount(response.account);
        setTokenExpirationFromClaims(response.idTokenClaims);
        // Clear redirect flag on successful redirect response
        sessionStorage.removeItem(REDIRECT_ATTEMPTED_KEY);
      } else if (!getMsalInstance().getActiveAccount() && getMsalInstance().getAllAccounts().length > 0) {
        const account = getMsalInstance().getAllAccounts()[0];
        logger.debug("[Auth] Using cached account:", account.username);
        getMsalInstance().setActiveAccount(account);
        setTokenExpirationFromClaims(account.idTokenClaims);
      }

      getMsalInstance().enableAccountStorageEvents();
      cbId = getMsalInstance().addEventCallback((event) => {
        if (
          event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          event.eventType === EventType.SSO_SILENT_SUCCESS
        ) {
          if (event?.payload?.account) {
            getMsalInstance().setActiveAccount(event.payload.account);
          }
        }
      });

      // Register auth functions BEFORE marking as initialized
      registerAuthTokenFunction(getValidAccessToken);
      registerTokenRefreshFunction(refreshToken);
      registerLogoutFunction(logout);
      logger.debug("[Auth] Auth functions registered");

      // Mark initialization as complete
      setInitialized(true);
      setLoading(false);
      logger.debug("[Auth] Initialization complete");
    })();
    return () => {
      if (cbId) getMsalInstance().removeEventCallback(cbId);
      if (isEmbedMode) destroyEmbedAuth();
    };
  }, [getValidAccessToken, refreshToken, logout]);

  const value = useMemo(() => ({
    token,
    loading,
    initialized,
    embedAuthError,
    login,
    logout,
    getValidAccessToken,
    refreshToken,
    isAuthenticated
  }), [token, loading, initialized, embedAuthError, login, logout, getValidAccessToken, refreshToken, isAuthenticated]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
